"use client";

import { BrandSearch } from "@/components";
import { CustomCheckbox } from "@/components/Checkbox/CheckboxComponents";
import { useBrandNormalization } from "@/hooks";
import { useAnalytics } from "@/hooks/useAnalytics";
import { COLOR_OPTIONS, CONDITION_OPTIONS, DEAL_OPTIONS, DEFAULT_SORT_KEY, HARDWARE_OPTIONS, SORT_OPTIONS, VENDOR_OPTIONS } from "@/utils";
import React, { useCallback, useEffect, useState } from "react";
import ChevronIcon from "./ChevronIcon";

interface Filters {
  sortOrder: string;
  minPrice: string;
  maxPrice: string;
  brands: string[];
  hardwares: string[];
  conditions: string[];
  colors: string[];
  vendors: string[];
  deals: string[];
}

export const DROPDOWN_SECTION_KEYS = [
  "brands",
  "deals",
  "price",
  "colors",
  "hardwares",
  "conditions",
  "vendors",
] as const;

type DropdownSectionKey = typeof DROPDOWN_SECTION_KEYS[number];
type DropdownSection = Record<DropdownSectionKey, boolean>;

interface FilterPanelProps {
  onApplyFilters: (filters: Filters) => void;
  brands: Set<string> | string[];
  currentFilters: Filters;
  isMobile?: boolean;
  onClose?: () => void;
  initialTab?: "sort" | "filter";
}

interface DropdownSectionProps {
  title: string;
  sectionKey: DropdownSectionKey;
  isOpen: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  children: React.ReactNode;
}

const DropdownSection: React.FC<DropdownSectionProps> = ({
  title,
  isOpen,
  onToggle,
  isMobile = false,
  children,
}) => (
  <div>
    <button
      onClick={onToggle}
      className={`w-full flex items-center justify-between py-2 text-left ${isMobile ? "" : "ml-4 mr-4"}`}
    >
      <span className="text-sm font-raleway tracking-[0.1em]">
        {title}
      </span>
      <ChevronIcon
        className={`transform transition-transform ${isOpen ? "" : "rotate-180"}`}
      />
    </button>
    {isOpen && (
      <div className={`pb-3 pr-1 mt-4 ${isMobile ? "" : "pl-8"}`}>
        {children}
      </div>
    )}
  </div>
);

interface CheckboxOptionProps {
  value: string;
  label: string;
  checked: boolean;
  onChange: (value: string, checked: boolean) => void;
}

const CheckboxOption: React.FC<CheckboxOptionProps> = ({
  value,
  label,
  checked,
  onChange,
}) => (
  <label 
    className="flex items-center space-x-2 cursor-pointer py-2"
    onClick={() => onChange(value, !checked)}
  >
    <CustomCheckbox checked={checked} className="w-5 h-5" />
    <span className="text-sm font-raleway tracking-[0.1em]">{label}</span>
  </label>
);

const FilterPanel: React.FC<FilterPanelProps> = ({
  onApplyFilters,
  brands = [],
  currentFilters,
  isMobile = false,
  onClose,
  initialTab = "sort",

}) => {
  const { logEvent } = useAnalytics();
  const [filters, setFilters] = useState<Filters>(currentFilters);
  const [openSections, setOpenSections] = useState<DropdownSection>({
    brands: false,
    deals: false,
    price: false,
    colors: false,
    hardwares: false,
    conditions: false,
    vendors: false,
  });
  const [brandSearch, setBrandSearch] = useState("");
  const [activeTab, setActiveTab] = useState<"sort" | "filter">(initialTab);

  const applyFilters = useCallback((newFilters: Filters) => {
    onApplyFilters(newFilters);
  }, [onApplyFilters]);

  useEffect(() => {
    setFilters(currentFilters);

    setOpenSections((prev) => {
      const newSections: DropdownSection = { ...prev };
      DROPDOWN_SECTION_KEYS.forEach((key) => {
        if (key === "price") {
          newSections[key] = !!(currentFilters.minPrice || currentFilters.maxPrice);
        } else if (
          Array.isArray((currentFilters)[key]) &&
          (currentFilters[key] as string[]).length > 0
        ) {
          newSections[key] = true;
        } else {
          newSections[key] = false;
        }
      });
      return newSections;
    });
  }, [currentFilters]);

  const brandArray = Array.isArray(brands) ? brands : Array.from(brands);
  const normalizedBrands = useBrandNormalization(brandArray, filters.brands);

  const updateBrandsIfNeeded = useCallback(() => {
    if (brandArray.length > 0 &&
      (normalizedBrands.length !== filters.brands.length ||
        normalizedBrands.some((brand: string, index: number) => brand !== filters.brands[index]))) {
      const normalizedFilters = { ...filters, brands: normalizedBrands };
      setFilters(normalizedFilters);
      applyFilters(normalizedFilters);
    }
  }, [brandArray.length, normalizedBrands, filters, applyFilters]);

  useEffect(() => {
    updateBrandsIfNeeded();
  }, [updateBrandsIfNeeded]);

  const handleFilterChange = useCallback((key: keyof Filters, value: string | string[]) => {
    const oldValue = filters[key];
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    const totalActiveFilters = Object.keys(newFilters).filter(filterKey => {
      const filterValue = newFilters[filterKey as keyof Filters];
      return Array.isArray(filterValue) ? filterValue.length > 0 : filterValue && filterValue !== DEFAULT_SORT_KEY;
    }).length;

    logEvent("filter_applied", {
      filter_type: key,
      filter_value: value,
      old_value: oldValue,
      total_active_filters: totalActiveFilters,
      is_mobile: isMobile,
      context: key === 'sortOrder' ? 'sort' : 'filter'
    });

    if (key === 'sortOrder') {
      applyFilters(newFilters);
      if (onClose) onClose();
    } else {
      applyFilters(newFilters);
    }
  }, [filters, applyFilters, onClose, logEvent, isMobile]);

  const handleArrayFilterChange = useCallback((
    key: keyof Filters,
    value: string,
    checked: boolean
  ) => {
    const currentArray = filters[key] as string[];
    let newArray;

    if (checked) {
      newArray = [...currentArray, value];
    } else {
      if (key === 'brands') {
        newArray = currentArray.filter((item) => item.toLowerCase() !== value.toLowerCase());
      } else {
        newArray = currentArray.filter((item) => item !== value);
      }
    }

    const newFilters = { ...filters, [key]: newArray };

    const totalActiveFilters = Object.keys(newFilters).filter(filterKey => {
      const filterValue = newFilters[filterKey as keyof Filters];
      return Array.isArray(filterValue) ? filterValue.length > 0 : filterValue && filterValue !== DEFAULT_SORT_KEY;
    }).length;

    logEvent("filter_applied", {
      filter_type: key,
      filter_value: value,
      filter_action: checked ? 'add' : 'remove',
      total_selected: newArray.length,
      total_active_filters: totalActiveFilters,
      is_mobile: isMobile,
      context: 'filter'
    });

    setFilters(newFilters);
    applyFilters(newFilters);
  }, [filters, applyFilters, logEvent, isMobile]);

  const handleReset = useCallback(() => {
    const oldFiltersCount = Object.keys(filters).filter(key => {
      const value = filters[key as keyof Filters];
      return Array.isArray(value) ? value.length > 0 : value && value !== DEFAULT_SORT_KEY;
    }).length;

    const resetFilters: Filters = {
      sortOrder: DEFAULT_SORT_KEY,
      minPrice: "",
      maxPrice: "",
      brands: [],
      hardwares: [],
      conditions: [],
      colors: [],
      vendors: [],
      deals: [],
    };

    logEvent("filter_reset", {
      previous_active_filters: oldFiltersCount,
      is_mobile: isMobile,
      context: 'filter_panel'
    });

    setFilters(resetFilters);
    applyFilters(resetFilters);
  }, [applyFilters, filters, logEvent, isMobile]);

  const handleApplySort = useCallback(() => {
    applyFilters(filters);
    if (onClose) onClose();
  }, [filters, applyFilters, onClose]);

  const handleApplyFilter = useCallback(() => {
    applyFilters(filters);
    if (onClose) onClose();
  }, [filters, applyFilters, onClose]);

  const toggleSection = useCallback((section: DropdownSectionKey) => {
    const wasOpen = openSections[section];

    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));

    logEvent("filter_section_toggle", {
      section,
      action: wasOpen ? 'close' : 'open',
      is_mobile: isMobile,
      context: 'filter_panel'
    });
  }, [openSections, logEvent, isMobile]);

  const filteredBrands = brandArray.filter((brand) =>
    brand.toLowerCase().includes(brandSearch.toLowerCase())
  );

  return (
    <div
      className={`bg-white ${isMobile
        ? "fixed inset-0 z-[100] flex flex-col"
        : "min-w-[320px] max-w-[400px]"
        }`}
    >
      {isMobile && (
        <div className="">
          <div className="flex items-center justify-between p-4">
            <div className="w-6"></div>
            <button onClick={onClose} className="text-md">
              ✕
            </button>
          </div>
          <div className="mx-4">
            <div className="flex border-b border-light-grey gap-3">
              <button
                onClick={() => setActiveTab("sort")}
                className={`py-3 px-2 text-center font-raleway font-semibold text-sm border-b-2 ${activeTab === "sort"
                  ? "border-black text-black"
                  : "border-transparent text-light-grey"
                  }`}
              >
                SORT BY
              </button>
              <button
                onClick={() => setActiveTab("filter")}
                className={`py-3 px-2 text-center font-raleway font-semibold text-sm border-b-2 ${activeTab === "filter"
                  ? "border-black text-black"
                  : "border-transparent text-light-grey"
                  }`}
              >
                FILTER BY
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`${isMobile ? "flex-1 overflow-y-auto p-4" : ""} space-y-2`}
      >
        {(!isMobile || activeTab === "sort") && (
          <div className="mb-12 ml-4 mr-4">
            {!isMobile && (
              <div className="block text-md mb-6 font-raleway text-black" style={{ fontWeight: 700 }}>
                Sort By
              </div>
            )}
            <div className="space-y-3">
              {SORT_OPTIONS.map(({ value, label }) => (
                                  <label
                    key={value}
                    className={`flex items-center space-x-3 cursor-pointer py-2 ${isMobile ? "" : "ml-4 mr-4"}`}
                  >
                  <input
                    type="radio"
                    name="sortOrder"
                    value={value}
                    checked={filters.sortOrder === value}
                    onChange={(e) =>
                      handleFilterChange("sortOrder", e.target.value)
                    }
                    className="form-radio text-black peer-checked:bg-black peer-checked:border-black w-5 h-5"
                    style={{ accentColor: '#1c1c1c' }}
                  />
                  <span className="text-sm font-raleway tracking-[0.1em]">
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {(!isMobile || activeTab === "filter") && (
                      <div className={`${isMobile ? "" : "ml-4 mr-4"}`}>
            {!isMobile && (
              <div className="block text-md mb-6 font-raleway text-black" style={{ fontWeight: 700 }}>
                Filter By
              </div>
            )}

            <div className="space-y-3">
            {/* Brand Section - Special case with search */}
            {brandArray.length > 0 && (
              <DropdownSection
              title="BRAND"
              sectionKey="brands"
              isOpen={openSections.brands}
              onToggle={() => toggleSection("brands")}
              isMobile={isMobile}
            >
                <div className="mb-3 mt-4">
                  <BrandSearch
                    onChange={setBrandSearch}
                    placeholder="Search"
                  />
                </div>
                <div className="max-h-32 overflow-y-auto space-y-3">
                  {filteredBrands.map((brand) => (
                    <CheckboxOption
                      key={brand}
                      value={brand}
                      label={brand}
                      checked={filters.brands.some(filterBrand => filterBrand.toLowerCase() === brand.toLowerCase())}
                      onChange={(value, checked) => handleArrayFilterChange("brands", value, checked)}
                    />
                  ))}
                </div>
              </DropdownSection>
            )}

            {/* Deal Section */}
            <DropdownSection
              title="DEAL"
              sectionKey="deals"
              isOpen={openSections.deals}
              onToggle={() => toggleSection("deals")}
              isMobile={isMobile}
            >
                <div className="space-y-3">
                  {DEAL_OPTIONS.map(({ value, label }) => (
                  <CheckboxOption
                    key={value}
                    value={value}
                    label={label}
                    checked={filters.deals?.includes(value)}
                    onChange={(value, checked) => handleArrayFilterChange("deals", value, checked)}
                  />
                ))}
              </div>
            </DropdownSection>

            {/* Price Section */}
            <DropdownSection
              title="PRICE"
              sectionKey="price"
              isOpen={openSections.price}
              onToggle={() => toggleSection("price")}
              isMobile={isMobile}
            >
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange("minPrice", e.target.value)}
                  className="w-full h-9 pl-3 pr-1 border border-gray-300 focus:border-gray-400 font-raleway text-sm outline-none bg-white"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange("maxPrice", e.target.value)}
                  className="w-full h-9 pl-3 pr-1 border border-gray-300 focus:border-gray-400 font-raleway text-sm outline-none bg-white"
                />
              </div>
            </DropdownSection>

            {/* Color Section */}
                        <DropdownSection
              title="COLOR"
              sectionKey="colors"
              isOpen={openSections.colors}
              onToggle={() => toggleSection("colors")}
              isMobile={isMobile}
            >
                <div className="space-y-3">
                  {COLOR_OPTIONS.map(({ value, label }) => (
                  <CheckboxOption
                    key={value}
                    value={value}
                    label={label}
                    checked={filters.colors.includes(value)}
                    onChange={(value, checked) => handleArrayFilterChange("colors", value, checked)}
                  />
                ))}
              </div>
            </DropdownSection>

            {/* Hardware Section */}
                        <DropdownSection
              title="HARDWARE"
              sectionKey="hardwares"
              isOpen={openSections.hardwares}
              onToggle={() => toggleSection("hardwares")}
              isMobile={isMobile}
            >
                <div className="space-y-3">
                  {HARDWARE_OPTIONS.map(({ value, label }) => (
                  <CheckboxOption
                    key={value}
                    value={value}
                    label={label}
                    checked={filters.hardwares.includes(value)}
                    onChange={(value, checked) => handleArrayFilterChange("hardwares", value, checked)}
                  />
                ))}
              </div>
            </DropdownSection>

            {/* Condition Section */}
                        <DropdownSection
              title="CONDITION"
              sectionKey="conditions"
              isOpen={openSections.conditions}
              onToggle={() => toggleSection("conditions")}
              isMobile={isMobile}
            >
                <div className="space-y-3">
                  {CONDITION_OPTIONS.map(({ value, label }) => (
                  <CheckboxOption
                    key={value}
                    value={value}
                    label={label}
                    checked={filters.conditions.includes(value)}
                    onChange={(value, checked) => handleArrayFilterChange("conditions", value, checked)}
                  />
                ))}
              </div>
            </DropdownSection>

            {/* Vendor Section */}
            <DropdownSection
              title="VENDOR"
              sectionKey="vendors"
              isOpen={openSections.vendors}
              onToggle={() => toggleSection("vendors")}
              isMobile={isMobile}
            >
                <div className="space-y-3">
                  {VENDOR_OPTIONS.map(({ value, label }) => (
                  <CheckboxOption
                    key={value}
                    value={value}
                    label={label}
                    checked={filters.vendors.includes(value)}
                    onChange={(value, checked) => handleArrayFilterChange("vendors", value, checked)}
                  />
                ))}
              </div>
            </DropdownSection>
            </div>

            {!isMobile && (
              <div className="flex pt-6">
                <button
                  onClick={handleReset}
                  className="w-full py-[10px] px-3 opacity-50 bg-white overflow-hidden outline outline-1 outline-[#1C1C1C] outline-offset-[-1px] inline-flex justify-center items-center text-[#1C1C1C] text-base font-raleway font-[700] tracking-[1.6px] hover:bg-light-grey transition-colors"
                >
                  CLEAR FILTERS
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isMobile && (
        <div className="p-4 border-t border-gray-200">
          {activeTab === "sort" && (
            <button
              onClick={handleApplySort}
              className="w-full py-3 bg-white text-black border border-black font-raleway font-semibold"
            >
              APPLY SORT BY
            </button>
          )}
          {activeTab === "filter" && (
            <button
              onClick={handleApplyFilter}
              className="w-full py-3 bg-white text-black border border-black font-raleway font-semibold"
            >
              APPLY FILTER BY
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
